/**
 * Who may read HR's screens, who may change what is on them, and who may only
 * read them.
 *
 * HR is the administrator account, or a member of staff holding a position
 * with HR access, such as the Human Resources Manager: every administrator
 * right, while staying staff with pages of their own. Ask `isAdmin`, never the
 * role, which stays "employee" for them.
 *
 * An approver (the owner) sees everything HR sees and approves what waits on
 * HR (leave, salary sheets, letters, new joiners, resignations, change
 * requests) but changes no record. The screens hide what would change one;
 * the server refuses it all the same.
 */
import type { Session } from "@/lib/api/types";
import { useAuth } from "@/lib/auth/AuthProvider";

type Access = Pick<Session, "is_approver" | "can_view_admin" | "is_admin" | "hr_access"> & {
  user: Pick<Session["user"], "role">;
};

/** Every administrator right: the administrator account, or HR access. */
export function isAdmin(session: Access | null | undefined): boolean {
  if (!session) return false;
  return session.user.role === "admin" || Boolean(session.is_admin);
}

/** The administrator account itself, which alone gives HR access out. */
export function isAccountAdmin(session: Access | null | undefined): boolean {
  return session?.user.role === "admin";
}

/** An administrator, or an approver: HR's screens open for them. */
export function canViewAdmin(session: Access | null | undefined): boolean {
  if (!session) return false;
  return isAdmin(session) || Boolean(session.can_view_admin ?? session.is_approver);
}

/** An approver who is not also HR: reads and approves, changes nothing. */
export function isReadOnly(session: Access | null | undefined): boolean {
  if (!session) return false;
  return !isAdmin(session) && Boolean(session.is_approver);
}

/**
 * A member of staff who also reads HR's screens: an approver, or somebody
 * with HR access. They move between the two, so each shell offers the other.
 */
export function hasBothShells(session: Access | null | undefined): boolean {
  return canViewAdmin(session) && !isAccountAdmin(session);
}

/** Whether this person may approve or decline what waits on HR. */
export function canApprove(session: Access | null | undefined): boolean {
  return canViewAdmin(session);
}

/** True for an approver on HR's screens: hide what changes a record. */
export function useReadOnly(): boolean {
  return isReadOnly(useAuth().session);
}

export function useCanViewAdmin(): boolean {
  return canViewAdmin(useAuth().session);
}

export function useIsAdmin(): boolean {
  return isAdmin(useAuth().session);
}
